/* eslint-disable @next/next/no-img-element */
import { useMemo } from 'react';
import { Carousel, Col, Row } from 'antd';

import textConstant from 'src/constants/text';
import FormContactUs from 'src/containers/Forms/Contact';
import { ContactOfficeContact, useContactOffice, useContactTopic } from 'src/hooks';
import { ContactType, getContactHref, getContactType } from 'src/utils/contact';

import classes from './index.module.css';
import cn from 'src/utils/cn';

const contactIcons: Partial<Record<ContactType, string>> = {
	whatsapp: '/icons/contact/whatsapp.svg',
	email: '/icons/contact/email.svg',
	phone: '/icons/contact/phone.svg',
	website: '/icons/contact/website.svg',
};

const ContactItem = ({
	contact,
	classNames,
}: {
	contact: ContactOfficeContact;
	classNames?: string;
}) => {
	const contactType = getContactType(contact.contact_name);
	const contactIcon = contactIcons[contactType];
	const href = getContactHref(contactType, contact.contact_value);
	const isExternalLink = contactType === 'whatsapp';

	const content = (
		<>
			{contactIcon && <img src={contactIcon} alt={`${contact.contact_name} icon`} />}
			<span className="text-sm">
				{contact.contact_name}: {contact.contact_value}
			</span>
		</>
	);

	if (!href) {
		return <div className={cn(classes['callme-text'], classNames)}>{content}</div>;
	}

	return (
		<a
			className={cn(classes['callme-text'], classNames)}
			href={href}
			target={isExternalLink ? '_blank' : undefined}
			rel={isExternalLink ? 'noopener noreferrer' : undefined}
		>
			{content}
		</a>
	);
};

const mainOfficeTextClassName = cn(
	'font-semibold text-[18px] text-neutral-3 my-4!',
	'max-md:max-w-[350px] max-md:mx-auto',
	'max-sm:m-0 max-sm:text-left',
);

const PageContainer = () => {
	const { data: topicData, isLoading: isTopicLoading } = useContactTopic();
	const { data: officeData, isLoading: isOfficeLoading } = useContactOffice();
	const office = officeData?.data?.record;
	const topic = topicData?.data?.records;
	const officeImages = useMemo(
		() => [...(office?.url_images?.bsd ?? []), ...(office?.url_images?.pulomas ?? [])],
		[office?.url_images?.bsd, office?.url_images?.pulomas],
	);

	if (isTopicLoading || isOfficeLoading) {
		return (
			<div className={classes['container-page']}>
				<div className="container">Loading...</div>
			</div>
		);
	}

	return (
		<div className={classes['container-page']}>
			<div className="container">
				<Row gutter={32}>
					<Col lg={{ span: 16 }} md={{ span: 14 }} sm={{ span: 24 }} xs={{ span: 24 }}>
						<div className={classes['form-contact-us-wrap']}>
							<div className={classes['form-greeting']}>
								<h3 className="mb-4">{textConstant.CONTACT_US.PAGE_GREETING}</h3>
								<p>{textConstant.CONTACT_US.PAGE_GREETING_DESC}</p>
							</div>
							<FormContactUs topic={topic} />
						</div>
					</Col>
					<Col lg={{ span: 8 }} md={{ span: 10 }} sm={{ span: 24 }} xs={{ span: 24 }}>
						<div className={classes['office-content']}>
							<Carousel className={classes['image-slider']} effect="fade" autoplay>
								{officeImages.map((image, index) => {
									return (
										<div key={`${image}-${index}`}>
											<div
												className={classes['image-wrap']}
												style={{ backgroundImage: `url(${image})` }}
											/>
										</div>
									);
								})}
							</Carousel>
							<div>
								<p className={mainOfficeTextClassName}>{office?.title}</p>
								<p className={mainOfficeTextClassName}>{office?.name}</p>
							</div>
							{office?.office_area.map((area) => {
								return (
									<div key={area.office_name} className={classes['wrap-office-area']}>
										<span className={classes['office-area-name-text']}>{area.office_name}</span>
										<div className={classes['address-text']}>
											{area.office_address_detail && (
												<div dangerouslySetInnerHTML={{ __html: area.office_address_detail }} />
											)}
											{area.office_address && (
												<div dangerouslySetInnerHTML={{ __html: area.office_address }} />
											)}
											{area.office_address_area && (
												<div dangerouslySetInnerHTML={{ __html: area.office_address_area }} />
											)}
											{area?.website && (
												<ContactItem
													classNames="mt-2"
													contact={{
														contact_name: area?.website.title,
														contact_value: area?.website.link,
													}}
												/>
											)}
										</div>
									</div>
								);
							})}
							<div className={classes['contact-wrap']}>
								{office?.contact.map((contact) => (
									<ContactItem
										key={`${contact.contact_name}-${contact.contact_value}`}
										contact={contact}
									/>
								))}
							</div>
							{office?.consumer_complaint && (
								<div className={classes['consumer-complaint']}>
									{office.consumer_complaint.title && (
										<span className={classes['consumer-complaint-title']}>
											{office.consumer_complaint.title}
										</span>
									)}
									<span className="text-xs">
										{office.consumer_complaint.institution}
									</span>
									<span className="text-xs">
										{office.consumer_complaint.ministry}
									</span>
									<div className={classes['consumer-complaint-contact']}>
										<ContactItem contact={office.consumer_complaint} />
									</div>
								</div>
							)}
						</div>
					</Col>
				</Row>
			</div>
		</div>
	);
};

export default PageContainer;
